import React from 'react';
import Modal from '../../../shared/components/modals/Modal';
import './FinancialGovernanceHelpModal.css';

export default function FinancialGovernanceHelpModal({
  isOpen,
  onClose,
  highCostThresholdLabel = 'the configured threshold',
  variant = 'admin',
}) {
  const isManager = variant === 'manager';
  const isSuperAdmin = variant === 'superadmin';
  const modalTitle = isManager ? 'Financial summary' : 'Financial governance';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} size="large">
      <div className="fg-help">
        <p className="fg-help__lead">
          {isSuperAdmin ? (
            <>
              Metrics in this section are <strong>organization-wide</strong> (all locations) and use a
              {' '}<strong>calendar-year</strong> window (Jan 1 – Dec 31) unless noted otherwise.
              Annual budget is the <strong>sum of location budgets</strong>.
            </>
          ) : (
            <>
              Metrics in this section are scoped to <strong>this location</strong> and use a
              {' '}<strong>calendar-year</strong> window (Jan 1 – Dec 31) unless noted otherwise.
            </>
          )}
        </p>

        <section>
          <h3>Budget tracking</h3>
          <ul className="fg-help__list">
            <li>
              <strong>Annual Budget</strong> — {isSuperAdmin
                ? 'sum of location annual budgets. If none are set, utilization shows Unbudgeted.'
                : 'location budget set in configuration. If not set, utilization shows Unbudgeted.'}
            </li>
            <li>
              <strong>YTD Capex</strong> — approved device costs attributed to the calendar year
              (install / purchase / created date).
            </li>
            <li>
              <strong>YTD Opex</strong> — pro-rated value of active service contracts overlapping
              the calendar year.
            </li>
            <li>
              <strong>Utilization</strong> — (YTD Capex + YTD Opex) / Annual Budget × 100%.
            </li>
            <li>
              <strong>Remaining</strong> — Annual Budget minus calendar-year approved spend
              (YTD Capex + YTD Opex).
            </li>
          </ul>
        </section>

        <section>
          <h3>Pending approvals</h3>
          <ul className="fg-help__list">
            <li>
              Pending devices are <strong>excluded from YTD</strong> until approved.
            </li>
            <li>
              <strong>Budget Impact</strong> shows projected utilization if all pending items are
              approved.
            </li>
            <li>
              {isManager ? (
                <>
                  Devices you submit above {highCostThresholdLabel} are tracked as{' '}
                  <strong>high-cost submissions</strong> while awaiting admin approval.
                </>
              ) : isSuperAdmin ? (
                <>
                  Devices above each location&apos;s high-cost threshold are flagged organization-wide.
                  Location admins action these approvals. Use <strong>By location</strong> to see
                  pending count, value, and devices per site.
                </>
              ) : (
                <>
                  Devices above {highCostThresholdLabel} are flagged as high-cost and require priority
                  review.
                </>
              )}
            </li>
          </ul>
        </section>

        <section>
          <h3>Metric definitions</h3>
          <ul className="fg-help__list">
            <li>
              <strong>Total Capex</strong> — sum of device purchase costs for approved devices;
              excludes pending approvals.
            </li>
            <li>
              <strong>Total Maintenance Contracts</strong> — combined cost of active LAMC + CAMC
              contracts; excludes warranty and in-house maintenance.
            </li>
            <li>
              <strong>Annual Contract Cost</strong> — yearly expense for ongoing service contracts;
              used for OpEx projection and contract-to-value ratio.
            </li>
            <li>
              <strong>Contract to Value Ratio</strong> — (Annual Contract Cost / Total Capex) × 100.
              A ratio above 15% suggests expensive maintenance relative to device value.
            </li>
          </ul>
        </section>

        <section>
          <h3>Coverage and high-cost</h3>
          <ul className="fg-help__list">
            <li>
              <strong>Warranty</strong> — included at purchase; no separate contract cost.
            </li>
            <li>
              <strong>LAMC / CAMC</strong> — lifecycle and comprehensive annual maintenance contracts.
            </li>
            <li>
              <strong>In-house</strong> — no external coverage; maintained internally.
            </li>
            <li>
              <strong>Coverage Expiring</strong> — contracts ending within 30 days.
            </li>
            <li>
              <strong>Coverage Expired</strong> — past contract end date without renewal.
            </li>
            {isSuperAdmin && (
              <li>
                <strong>By location</strong> — location-level breakup of the same coverage
                metrics. Select a location to open Location Performance.
              </li>
            )}
          </ul>
        </section>

        {isSuperAdmin && (
          <section>
            <h3>Location breakdown</h3>
            <ul className="fg-help__list">
              <li>
                <strong>CAPEX</strong> — YTD device cost at that location (all-time in brackets).
              </li>
              <li>
                <strong>OPEX</strong> — YTD pro-rated contracts (LAMC/CAMC + warranty).
              </li>
              <li>
                <strong>Utilization</strong> — current-year spend vs allocated location budget.
              </li>
              <li>
                <strong>Efficiency</strong> — contract value as % of CAPEX.
              </li>
            </ul>
          </section>
        )}

        <section>
          <h3>Status colors</h3>
          <ul className="fg-help__list">
            <li>Green — 0–70% utilization (on track).</li>
            <li>Yellow — 71–90% utilization (watch).</li>
            <li>Red — above 90% utilization (at risk).</li>
            <li>
              Critical — overrun above 100%, or YTD Capex exceeds Annual Budget.
            </li>
          </ul>
        </section>
      </div>
    </Modal>
  );
}
